<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { $router } from '@/router'
import { $bus } from '@/common/events'
import { dialog } from '@/common/dialog'
import { toast } from '@/common/toast'
import { hcu } from '@/common/hcu'
import { $config, defaultServiceOptions } from '@/common/config'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useTitle } from '@/composables/title'
import { useTheme } from '@/composables/theme.js'
import { useAppState } from '@/composables/app.js'
import { useSession } from '@/composables/session'
import { useSettings } from '@/composables/settings'
import { canInterrupt } from '@/composables/interrupt'
import { useSessionTimer } from '@/composables/session'
import { useCabinetState } from '@/composables/cabinet'
import { onBarcodeReceived } from '@/composables/hardware/barcode'
import { onSmartCardReceived, onSmartCardUnmounted } from '@/composables/hardware/smartcard'
import { onWeightScaleStatus } from '@/composables/hardware/weightScale'
import { useTranslate, supportedLanguages } from '@/composables/language'
import { LogService } from '@/services/LogService.js'
import { AuthService } from '@/services/AuthService.js'
import { ConfigService } from '@/services/ConfigService.js'
import { CabinetService } from "@/services/CabinetService.js";
import { UserService } from "@/services/UserService.js";
import { useAuthState } from '@/composables/auth'
import KeyboardNumpad from '@/components/KeyboardNumpad.vue'
import LanguageSelector from '@/components/LanguageSelector.vue'
import DropDown from '@/components/DropDown.vue'
import DebugFooter from '@/components/Maintenance/DebugFooter.vue'

onWeightScaleStatus(() => {
  //Mounted
});

let logService = new LogService();
let authService = new AuthService();
let configService = new ConfigService();
let cabinetService = new CabinetService();
let userService = new UserService();

const route = useRoute();
const { t, translateError, setLanguage, isLanguageSupported, locale } = useTranslate();

const languageOptions = computed(() => supportedLanguages.map(code => {
  let label = t(`languages.${code}`);
  if (label === `languages.${code}`) label = code.toUpperCase();
  return { id: code, name: label };
}));
const currentLang = computed({
  get() { return locale.value; },
  set(v) { setLanguage(v); },
});

function openLegalNotice(e) {
  if (e && e.preventDefault) e.preventDefault();
  $router.push({ name: "LegalNotice" });
}
const { isDark } = useTheme();
const { appState } = useAppState();
const { settings } = useSettings();
const { cabinetState } = useCabinetState();

const authStore = useAuthState();

const mode = ref("upr"); //numpad, upr (username and password or rfid/barcode), password, 2fa, rfid
const submode = ref("username"); //username, password, 2fa

const username = ref("");
const password = ref("");
const tfa = ref("");
const pin = ref("");
const error = ref();

const allowAnonymous = ref(false);
const allowUsername = ref(false);
const allowBarcode = ref(false);
const allowRFID = ref(false);

const isLoadingDelay = ref(true);
const userDisplayName = ref(null);
const passwordAttempts = ref(0);
const loginAttempts = ref(0);

const userGroups = ref(null);
const partialResult = ref(null);
const masterCabinetId = ref(null);

let context2fa = null;
let requires2FA = null;
let retryTimer = null;
let once = true;

let passwordTimeout = null;
let passwordEntryDuration = 120 * 1000; //2 minutes

let initSessionTimer = null;

onMounted(async () => {
  useTitle(t('login.title'));
  useMenu(false);
  useSubmenu(false);
  useTopBar(false);

  canInterrupt(true);
  useSessionTimer(false);
  
  //Temporary work-around in case other components load with delay
  initSessionTimer = setTimeout(() => {
    useSessionTimer(false);
  }, 300);

  mode.value = "upr";
  submode.value = "username";

  await autoLogout();

  masterCabinetId.value = cabinetState.value?.id;

  //onMounted will be called only when ready, no need to re-check
  loadLoginMethods();

  //TODO: Get master cabinet id upon load - used for logging
  try {
    let { controllers } = await hcu.cabinet().getInstances2();
    let master = controllers.find(x => x.master);
    if (master) {
      masterCabinetId.value = master.id;
      console.log("[app] Master cabinet id", masterCabinetId.value);
    }
  }
  catch (err) {
    console.error(err);
  }
});

onUnmounted(async () => {
  clearTimeout(initSessionTimer);
});

async function loadLoginMethods(options) {
  options = options || { };
  let quiet = !!options.quiet;

  try {
    //Get config from YAML file
    const cabinetId = masterCabinetId.value;
    if (!cabinetId) {
      console.error("[login] cabinet id not defined");
      throw new Error(t('login.error_cabinet_not_configured'));
    }

    //Get cabinet data from control panel
    let cabinet = await cabinetService.getCabinet(cabinetId);
    if (!cabinet) {
      throw new Error(t('login.error_db_not_responding')); //ali omara je izbrisana, nedosegljiva, nedefinirana
    }

    //Get user groups and permissions from control panel
    let groups = await userService.getUserGroups();
    if (!groups) {
      throw new Error(t('login.error_db_not_responding')); //ali omara je izbrisana, nedosegljiva, nedefinirana
    }

    //Save groups to a global variable
    //$global.userGroups = groups.items; //TODO: use composables
    userGroups.value = groups.items;

    //Allowed log in methods as defined in control panel
    allowAnonymous.value = !!cabinet.authMethods?.anonymous;
    allowUsername.value = !!cabinet.authMethods?.username || !!cabinet.authMethods?.email;
    allowBarcode.value = !!cabinet.authMethods?.barcode;
    allowRFID.value = !!cabinet.authMethods?.smartcard;

    //Loading animation goes off
    isLoadingDelay.value = false;

    //Clear errors
    error.value = null;

    //Clear retry interval
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  }
  catch (err) {
    //Loading animation goes off
    isLoadingDelay.value = false;

    //if (!quiet) {
      if (once) {
        console.error("[login] login methods", err)
        once = false;
      }
      error.value = err.message;
      err = translateError(err);
      if (err.isSyntaxError || err.isNetworkError) {
        error.value = t('login.error_server');
      }
      /*
      else {
        toast({
          type: "danger",
          title: t('login.error_title'),
          text: err.message,
          confirmButtonText: t('login.dialog_ok'),
          //cancelButtonText: t('generic.cancel'),
          timeout: 30 * 1000
        });
      }
      */
    //}

    //Start retry timer
    if (!retryTimer) {
      retryTimer = setInterval(function() {
        loadLoginMethods({ quiet: true });
      }, 5000);
    }

  }
}

async function autoLogout() {
  //Logout
  try {
    let sessionId = $config.sessionId;
    if (sessionId) {
      let timeout = route.query?.timeout == "true";
      let closeTray = route.query?.closeTrayAutoLogout == "true";

      try {
        if (appState.isLogEnabled) {
          logService.insertLog({
            a: "user.logout",
            t: "audit",
            s: "info",
            cid: masterCabinetId.value,
            uid: authStore.userId.value,
            sid: authStore.sessionId.value,
            msg: closeTray ? "Avtomatska odjava uporabnika iz omare po zaprtju predala." : (timeout ? "Odjava uporabnika iz omare. Seja je potekla." : "Odjava uporabnika iz omare"),
            //data: result,
            ts: new Date()
          }).then(console.log).catch(console.error);
        }
      }
      catch (err) {
        console.error("afterLogin", err);
      }

      //Clear session from local store
      authStore.logout({ sessionTimeout: timeout });

      //Clear session on the server side
      let result = await authService.logout({ sessionId, timeout });

      configService.clearFlag("session").then(() => { }).catch(() => { });

      if (closeTray) {
        toast(t('login.auto_logout_toast'));
      }
      
      if (result.error) {
        throw new Error(result.error);
      }
    }
  }
  catch (err) {
    console.error(err);
  }
}

function resetState() {
  mode.value = "upr"; //TODO: rename to step (step by step or multipart)
  submode.value = "username"; //TODO: rename to step
  username.value = "";
  password.value = "";
  tfa.value = "";
  pin.value = "";
  partialResult.value = null;
  isLoadingDelay.value = false;
  userDisplayName.value = null;
  passwordAttempts.value = 0;
  loginAttempts.value = 0;
}

function afterLogin() {
  try {
    console.log("[login] afterLogin");

    //Clear all pop-up messages
    toast({ clearAll: true });

    loginAttempts.value = 0;

    let permissions = authStore?.context?.permissions || [ ];
    let canLogin = permissions.includes("cabinet.login") || permissions.includes("cabinet.admin");
    if (!canLogin) {
      console.log("[login] User has no permissions to log in!", permissions);
      authStore.logout({ }); //TODO: Reason: uporabnik nima pravice
      dialog(t('login.error_no_permissions'));
      return;
    }
    
    //Log out from neighbour cabinets
    $bus.emit("logout other", { userId: authStore.userId.value });

    useSessionTimer(true);

    try {
      //TODO: Organization id
      if (appState.isLogEnabled) {
        logService.insertLog({
          a: "user.login",
          t: "audit",
          s: "info",
          cid: masterCabinetId.value,
          uid: authStore.userId.value,
          sid: authStore.sessionId.value,
          msg: "Prijava uporabnika v omaro",
          //data: result,
          ts: new Date()
        }).then(console.log).catch(console.error);
      }
    }
    catch (err) {
      console.error("afterLogin", err);
    }

    configService.setFlag("session").then(() => { }).catch(() => { });

    const { settings } = useSettings();

    // console.log("emit force reload")
    // $bus.emit("force reload side menu");
    const { showUserItemsDisplayedOnce } = useSession();
    showUserItemsDisplayedOnce.value = false; //Reset the flag

    //Special use case
    if (settings.value?.cabinet?.mode == "gloves") {
      let canUseGloves = permissions.includes("useCase.gloves.pickupItem") || permissions.includes("useCase.gloves.returnItem");
      let canLoadGloves = permissions.includes("useCase.gloves.load") || permissions.includes("useCase.gloves.change");
      let canAccessControlPanel = permissions.includes("useCase.gloves.overview") || permissions.includes("useCase.gloves.report") || permissions.includes("useCase.gloves.allowExtra");

      if (canUseGloves) {
        $router.push({ name: "GlovesCabinet", query: { first: "login" } });
      }
      else if (canLoadGloves) {
        $router.push({ name: "GlovesStockTaking", query: { first: "login" } });
      }
      else {
        $router.push({ name: "History", query: { first: "login" } }); 
      }
      //TODO: Redirect to control panel
      // else {
      //   $router.push({ name: "NoPermissions" }); 
      // }
    }
    else {
      //TODO: According to user's permissions; can be service mode only
      let catalogRoute = settings.value?.debug?.catalog2 ? "Catalog2" : "Catalog";
      $router.push({ name: catalogRoute, query: { first: "login" } });
      console.log("[login] afterLogin redirect to Catalog");
    }

    console.log("[login] afterLogin done");
  }
  catch (err) {
    console.error("[login]", err);
    toast(err);
  }
}

function setPasswordEntryTimeout() {
  clearPasswordEntryTimeout();
  passwordTimeout = setTimeout(stepBack, passwordEntryDuration);
}

function clearPasswordEntryTimeout() {
  if (passwordTimeout) {
    clearTimeout(passwordTimeout);
    passwordTimeout = null;
  }
}

onBarcodeReceived((data) => {
  console.log("barcode login", data);
  loginBarcode(data.code);
});

onSmartCardReceived((data) => {
  console.log("[login] smartcard", data);
  loginRFID125(data.code);
});

onSmartCardUnmounted((data) => {
  //Cancel PIN entry and reset to initial state
  resetState();
});

function onUserActivity() {
  //Extend timer on user activity
  setPasswordEntryTimeout();
}

function setLoginInProgress(isInProgress) {
  let canInterrupt = !isInProgress;
  $bus.emit("interrupt promise", { canInterrupt });
}

async function setPin2FA(result) {
  partialResult.value = result;
  passwordAttempts.value = 0;

  //Set timeout
  setPasswordEntryTimeout();

  //Clear PIN code (in case if entered in previous attempt)
  pin.value = "";

  //Display loading animation
  isLoadingDelay.value = true;

  //Switch to PIN code view
  submode.value = "pin";

  //Hide loading animation
  setTimeout(function() {
    isLoadingDelay.value = false;
    setLoginInProgress(false);
  }, 2000);
}

//Perform login with barcode
async function loginBarcode(barcode) {
  try {
    if (typeof barcode == "number") {
      barcode = barcode.toString();
    }

    //Mode "upr" = username + password or RFID (barcode included)
    if (/* mode.value == "upr" && */ typeof barcode == "string" && barcode && barcode.length > 0) {
      setLoginInProgress(true);

      let result = await authService.login({ barcode: barcode });
      if (result.error) {
        throw new Error(result.error);
      }

      console.log("barcode result", result)
      $config.sessionId = result.session.id;

      //userDisplayName.value = result.user?.displayName; //Do NOT display real name unless fully logged in
      userDisplayName.value = result.user?.username; //Instead, display nick name or ID number or something less personal (due to GDPR)

      //Check if PIN code is required
      if (result.tfa == "pin") {
        // context2fa = { };
        // context2fa.session = result.session;
        // context2fa.user = result.user;
        // context2fa.jwt = result.jwt;

        //Go to 2FA step
        setPin2FA(result)
      }
      else {
        //Logged in
        authStore.context.session = result.session;
        authStore.context.user = result.user;
        authStore.context.jwt = result.jwt;
        authStore.context.permissions = getUserPermissions(result.user);
        authStore.save();

        $bus.emit("login", { displayName: result.user?.displayName, username: result.user?.username });
        
        setLoginInProgress(false);
        afterLogin();
      }
    }
  }
  catch (err) {
    increaseLoginAttempts(err);
    setLoginInProgress(false);

    dialog({
      type: "danger",
      //title: "Prijava uporabnika",
      text: err.message,
      confirmButtonText: t('generic.ok'),
      //cancelButtonText: "Prekliči",
      timeout: 3000
    });
  }
}

function increaseLoginAttempts(err) {
  loginAttempts.value += 1;
  if (loginAttempts.value == 5) {
    try {
      if (appState.isLogEnabled) {
        logService.insertLog({
          a: "user.loginAttempt",
          t: "audit",
          s: "error",
          cid: masterCabinetId.value,
          msg: "Večkrat neuspeli poskus prijave v omaro",
          ts: new Date()
        }).then(console.log).catch(console.error);
      }
    }
    catch (err) {
      console.error("[log] login attempt", err);
    }
  }
}

function getUserPermissions(user) {
  let permissions = [ ];
  try {
    console.log("getUserPermissions");
    if (user?.permissions) {
      permissions = user.permissions;
    }
    else if (userGroups.value && userGroups.value.length && user && user.groups && user.groups.length) {
      for (let groupId of user.groups) {
        let userGroup = userGroups.value.find(x => x.id == groupId);
        if (userGroup && userGroup.permissions && userGroup.permissions.length) {
          console.log("user in group", userGroup.name, "with", userGroup.permissions.length, "permissions");
          for (let permission of userGroup.permissions) {
            console.log("-", permission);
            if (!permissions.includes(permission)) {
              permissions.push(permission);
            }
          }
        }
      }
    }
  }
  catch (err) {
    console.error(err.message);
  }
  console.log("getUserPermissions result", permissions);
  return permissions;
}

//Perform login with RFID 125kHz
async function loginRFID125(code) {
  try {
    if (typeof code == "number") {
      code = code.toString();
    }

    //Mode "upr" = username + password or RFID (barcode included)
    if (/* mode.value == "upr" && */ typeof code == "string" && code && code.length > 0) {
      setLoginInProgress(true);

      if (!code) {
        throw new Error(t('login.error_card_not_read'));
      }

      let result = await authService.login({ rfid125: code });
      if (result.error) {
        throw new Error(result.error);
      }

      console.log("rfid125 result", result)
      $config.sessionId = result.session.id;

      //userDisplayName.value = result.user?.displayName; //Do NOT display real name unless fully logged in
      userDisplayName.value = result.user?.username; //Instead, display nick name or ID number or something less personal (due to GDPR)

      //Check if PIN code is required
      if (result.tfa == "pin") {
        // context2fa = { };
        // context2fa.session = result.session;
        // context2fa.user = result.user;
        // context2fa.jwt = result.jwt;

        console.log("rfid125 pin context 2fa", context2fa);

        //Go to 2FA step
        setPin2FA(result)
      }
      else {
        //Logged in
        authStore.context.session = result.session;
        authStore.context.user = result.user;
        authStore.context.jwt = result.jwt;
        authStore.context.permissions = getUserPermissions(result.user);
        authStore.save();

        $bus.emit("login", { displayName: result.user?.displayName, username: result.user?.username });
        
        setLoginInProgress(false);
        afterLogin();
      }
    }
    else {
      console.log("loginRFID125(code) else!", mode.value);
    }
    console.log("loginRFID125(code) through");
  }
  catch (err) {
    console.error("loginRFID125(code) error", err);
    increaseLoginAttempts(err);
    setLoginInProgress(false);

    toast(err);

    /*
    dialog({
      type: "danger",
      //title: "Prijava uporabnika",
      text: err.message,
      confirmButtonText: t('generic.ok'),
      //cancelButtonText: "Prekliči",
      timeout: 3000
    });
    */
  }
}

//Perform login
async function submit(e) {
  try {
    //Just in case if event is attached to the 'a' element with 'href'
    if (e && typeof e.preventDefault == "function") {
      e.preventDefault();
    }

    //Mode: username + password or RFID
    if (mode.value == "upr") {

      //Username
      if (submode.value == "username") {

        //Instant anonymous login without typing username and password
        if (allowAnonymous.value && username.value.trim().length == 0) {

          //Fictional session and user
          $config.sessionId = "anonymous";
          $config.session = { id: $config.sessionId };
          $config.user = { displayName: t('login.anonymous_user'), username: "Uporabnik", role: "admin" };

          let permissions = [
            { permission: "cabinet.login", name: "Prijava v omaro" },
            { permission: "cabinet.viewArticle", name: "Pregled artiklov" },
            { permission: "cabinet.useArticle", name: "Uporaba artiklov", includes: "cabinet.viewArticles" },
            { permission: "cabinet.editStock", name: "Upravljanje zaloge" },
            //{ permission: "cabinet.editProfile", name: "Urejanje lastnega profila" },
            { permission: "cabinet.history", name: "Pregled zgodovine" },
            { permission: "cabinet.service", name: "Servisni dostop" },
            //{ permission: "cabinet.impersonation", name: "Poosebitev uporabnika" },
            { permission: "cabinet.admin", name: "Administratorske pravice", includes: "cabinet.*" },
            { permission: "useCase.gloves.load", name: "Nalaganje rokavic v predale" },
            { permission: "useCase.gloves.change", name: "Menjava rokavic" },
            { permission: "useCase.gloves.report", name: "Oddajanje poročila" },
            { permission: "useCase.gloves.overview", name: "Pregled trenutnega stanja" },
            { permission: "useCase.gloves.allowExtra", name: "Odobritev dodatnih rokavic" },
            { permission: "useCase.gloves.pickupItem", name: "Izposoja rokavic iz predalov" },
            { permission: "useCase.gloves.returnItem", name: "Vračanje rokavic v predale" },
          ].map(x => x.permission);

          authStore.context.session = $config.session;
          authStore.context.user = $config.user;
          authStore.context.jwt = $config.jwt;
          authStore.context.permissions = permissions || getUserPermissions($config.user);
          authStore.save();

          return afterLogin();
        }

        let un = username.value;
        if (!un || un.trim().length == 0) {
          //throw new Error("Username is missing!");
          throw new Error(t('login.error_username'));
        }

        setLoginInProgress(true);

        let result = await authService.login({ username: un, suggestMethods: true });
        if (result.error) {
          throw new Error(result.error);
        }
        if (result.methods && result.methods.includes("password")) {
          userDisplayName.value = result.user?.displayName || username.value;
          submode.value = "password";
          password.value = "";
          passwordAttempts.value = 0;
          if (passwordTimeout) {
            clearTimeout(passwordTimeout);
          }
          passwordTimeout = setTimeout(stepBack, passwordEntryDuration);
          requires2FA = result.methods.includes("2fa");
          setTimeout(() => document.getElementById("password-textbox").focus(), 100);
          $bus.emit("login", { displayName: userDisplayName.value, username: result.user?.username });
          setLoginInProgress(false);
        }
        else {
          console.error("Unsupported method", result);
          throw new Error("Unsupported authentication method!");
        }
      }

      //Password
      else if (submode.value == "password") {
        let un = username.value;
        if (!un || un.trim().length == 0) {
          //throw new Error("Username is missing!");
          throw new Error(t('login.error_username'));
        }

        let pwd = password.value;
        if (!pwd || pwd.trim().length == 0) {
          //throw new Error("Password is missing!");
          throw new Error(t('login.error_password'));
        }

        passwordAttempts.value++;

        let result = await authService.login({ username: un, password: pwd });
        if (result.error) {
          throw new Error(result.error);
        }

        //Session info
        $config.sessionId = result.session.id;
        $config.session = result.session;
        $config.user = result.user;

        userDisplayName.value = result.user?.displayName;

        //TODO: Save session config to local storage

        if (requires2FA) {
          // context2fa = { };
          // context2fa.session = result.session;
          // context2fa.user = result.user;
          // context2fa.jwt = result.jwt;

          submode.value = "2fa";
        }
        else {
          if (passwordTimeout) {
            clearTimeout(passwordTimeout);
          }
          authStore.context.session = result.session;
          authStore.context.user = result.user;
          authStore.context.jwt = result.jwt;
          authStore.context.permissions = getUserPermissions(result.user);
          authStore.save();
          
          afterLogin();
        }
        setLoginInProgress(false);
      }

      //Two-factor authorization
      else if (submode.value == "2fa") {
        let code = tfa.value;
        if (!code || code.trim().length == 0) {
          throw new Error(t('login.error_two_factor_missing'));
        }

        let sessionId = $config.sessionId;
        let result = await authService.login2FA({ sessionId: sessionId, code: code, type: "otp" });
        if (result.error) {
          throw new Error(result.error);
        }
        setLoginInProgress(false);
        if (result.success || result.status == "success") {
          // console.log("context 2fa success", context2fa);
          // if (context2fa) {
          //   authStore.context.session = context2fa.session;
          //   authStore.context.user = context2fa.user;
          //   authStore.context.jwt = context2fa.jwt;
          //   authStore.context.permissions = getUserPermissions(context2fa.user);
          //   authStore.save();
          // }
          afterLogin();
        }
        else {
          console.error(reslut);
          dialog(new Error("Unknown login result"));
        }
      }

      //PIN code authorization
      else if (submode.value == "pin") {
        let code = pin.value;
        if (!code || code.trim().length == 0) {
          throw new Error(t('login.error_pin_missing'));
        }

        passwordAttempts.value++;

        let sessionId = $config.sessionId;
        let result = await authService.login2FA({ sessionId: sessionId, code: code, type: "pin" });
        if (result.error) {
          throw new Error(result.error);
        }

        if (result.status == "success") {
          if (passwordTimeout) {
            clearTimeout(passwordTimeout);
          }

          //console.log("context pin success", context2fa, partialResult.value);

          authStore.context.session = partialResult.value?.session;
          authStore.context.user = partialResult.value?.user;
          authStore.context.jwt = partialResult.value?.jwt;
          authStore.context.permissions = getUserPermissions(partialResult.value?.user);
          console.log("context pin permissions", partialResult.value?.user, authStore.context.permissions);
          authStore.save();

          $bus.emit("login", { displayName: partialResult.value?.user?.displayName || userDisplayName.value, username: partialResult.value?.user?.username });
          
          setLoginInProgress(false);
          afterLogin();
        }
        else {
          console.error(reslut);
          dialog(new Error("Unknown login result"));
        }
      }

    }
  }
  catch (err) {
    if (passwordAttempts.value >= 3) {
      stepBack();
    }

    increaseLoginAttempts(err);

    setLoginInProgress(false);

    //console.error(err);
    //dialog(err);
    toast(err);
  }
}

function stepBack() {
  mode.value = "upr";
  submode.value = "username";
  username.value = ""; //Clear
  password.value = ""; //Clear
  passwordAttempts.value = 0;
  loginAttempts.value = 0;
}

const canGoBack = computed(() => {
  return submode.value == "password" || submode.value == "2fa" || submode.value == "pin";
});
</script>

<template>
  <div class="relative dark:bg-gray-900">

    <!-- Go back -->
    <div class="fixed left-5 top-5" v-if="canGoBack">
      <Button type="primary" class="w-24 h-12" @click="stepBack()"><i class="fa fa-arrow-left mr-2"></i>{{ $t('generic.back') }}</Button>
    </div>

    <!-- Language chooser -->
    <div class="fixed right-5 top-5 z-10 w-40">
      <DropDown v-model="currentLang" :options="languageOptions" :name="t('legalNotice.languageLabel')" />
    </div>

    <!-- PIN code only -->
    <div v-if="mode == 'numpad'">
      <KeyboardNumpad :masked="true" :iconic="true" :max-length="8" style="width: 300px;"></KeyboardNumpad>
    </div>

    <!-- Username, password and other methods in consequential steps -->
    <div v-if="mode == 'upr'">

      <!-- Logo -->
      <div class="relative grid place-items-center pt-32 mb-16">
        <img src="../assets/img/logo-blue.png" alt="" class="w-48" v-if="!isDark && submode != 'password'" />
        <img src="../assets/img/logo-white.png" alt="" class="w-48" v-if="isDark && submode != 'password'" />
        <i class="fa fa-user fa-solid text-blue-900 text-center w-48" style="font-size: 100px" v-if="submode == 'password'"></i>
        <div class="absolute -bottom-7" v-if="submode == 'password' && userDisplayName">{{ userDisplayName }}</div>
      </div>

      <!-- Loading animation -->
      <div class="grid place-items-center mb-6" v-if="isLoadingDelay">
        <div>
          <Spinner size="10" style="margin: 0 auto;" />
          <!-- <div class="mt-5">{{ $t("intro.loading") }}</div> -->
        </div>
      </div>

      <!-- Error message -->
      <div class="grid place-items-center mb-6" v-if="error">
        <div>
          <div class="mt-5 text-red-500 dark:text-red-600">{{ error }}</div>
        </div>
      </div>

      <!-- Login form -->
      <div class="grid place-items-center mb-6" v-if="!isLoadingDelay">

        <!-- Prompt for username -->
        <div v-if="submode == 'username' && allowUsername">
          <!-- <label for="username" class="block mb-2 font-medium text-gray-900 dark:text-white">{{ $t('login.user_login_label') }}</label> -->
          <div class="flex flex-row w-72 items-group">
            <TextBox v-model="username" :placeholder="$t('login.username_placeholder')" @keypress.enter="submit" @enter="submit" class="w-full text-lg" />
            <Button type="primary" @click="submit"><i class="fa fa-arrow-right"></i></Button>
          </div>
        </div>
        
        <!-- Prompt for password (next step after username) -->
        <div v-if="submode == 'password'">
          <label for="password" class="block mb-2 font-medium text-gray-900 dark:text-white">{{ $t('login.enter_password_label') }}</label>
          <div class="flex flex-row w-72 items-group">
            <TextBox id="password-textbox" v-model="password" :masked="true" :placeholder="$t('login.password_placeholder')" @keypress.enter="submit" @enter="submit" class="w-full text-lg" />
            <Button type="primary" @click="submit"><i class="fa fa-arrow-right"></i></Button>
          </div>
        </div>

        <!-- Prompt for 2FA (next step after username/password) -->
        <label for="tfa" class="block mb-2 font-medium text-gray-900 dark:text-white" v-if="submode == '2fa'">{{ $t('login.two_factor_label') }}</label>
        <div class="flex flex-row w-72 items-group" v-if="submode == '2fa'">
          <TextBox v-model="tfa" :placeholder="$t('login.tfa_placeholder')" @keypress.enter="submit" class="w-full text-lg" />
          <Button type="primary" @click="submit"><i class="fa fa-arrow-right"></i></Button>
        </div>

        <!-- Prompt for PIN code (next step after barcode/smart card) -->
        <label for="pin" class="block mb-2 font-medium text-gray-900 dark:text-white" v-if="submode == 'pin'">{{ $t('login.pin_code_label') }}<span v-if="userDisplayName"> {{ $t('login.pin_for') }} {{ userDisplayName }}</span></label>
        <div class="flex flex-row gap-2" v-if="submode == 'pin'">
          <KeyboardNumpad v-model="pin" style="width: 300px;" :masked="true" :iconic="true" :max-length="8" @confirm="submit()" @keypress="onUserActivity()"></KeyboardNumpad>
        </div>
      </div>
      
      <!-- Alternative options: barcode, smart card -->
      <div class="text-center mb-6 dark:text-white" v-if="submode == 'username' && allowBarcode && allowRFID && !isLoadingDelay">
        <span v-if="allowUsername">{{ $t('login.show_card_barcode') }}</span>
        <span v-if="!allowUsername"><span class="text-lg">{{ $t('login.show_card_barcode_only') }}</span><br /><i class="fa fa-id-card fa-regular text-slate-400" style="font-size:90em"></i></span>
      </div>
      <div class="text-center mb-6 dark:text-white" v-if="submode == 'username' && allowBarcode && !allowRFID && !isLoadingDelay">
        <!--{{ $t('login.barcode_message') }}--> <!--&nbsp;<i class="fa fa-qrcode"></i> -->
        <span v-if="allowUsername">{{ $t('login.show_barcode') }}</span>
        <span v-if="!allowUsername"><span class="text-lg">{{ $t('login.show_barcode_only') }}</span><br /><i class="fa fa-barcode text-slate-400" style="font-size: 9em"></i></span>
      </div>
      <div class="text-center mb-6 dark:text-white" v-if="submode == 'username' && allowRFID && !allowBarcode && !isLoadingDelay">
        <span v-if="allowUsername">{{ $t('login.show_card') }}</span>
        <span v-if="!allowUsername"><span class="text-lg">{{ $t('login.show_card_only') }}</span><br /><i class="fa fa-id-card fa-regular text-slate-300" style="font-size: 9em"></i></span>
      </div>

    </div>

    <!-- Enter username and password -->
    <div v-if="mode == 'password'">
      <div class="mb-6">
        <label for="username" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Username</label>
        <input type="text" id="username" v-model="username" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" required>
      </div>
      <div class="mb-6">
        <label for="password" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Password</label>
        <input type="password" id="password" v-model="password" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" required>
      </div>
      <button type="button" @click="submit" class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">Log in</button>
    </div>

    <!-- Two-factor authentication method -->
    <div v-if="mode == '2fa'">
      <div class="mb-6">
        <label for="tfa" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Code</label>
        <input type="text" id="tfa" v-model="tfa" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" required>
      </div>
      <button type="button" @click="submit" class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">Confirm</button>
    </div>

    <!-- RFID card only -->
    <div v-if="mode == 'rfid'">
      <span class="text-lg font-bold dark:text-white">Prisloni kartico</span>
    </div>


    <!-- Footer: legal notice link -->
    <footer class="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 z-10 pointer-events-none">
      <a href="/legal-notice" @click="openLegalNotice"
       class="pointer-events-auto hover:text-gray-700 dark:hover:text-gray-200 underline">
      {{ t('legalNotice.title') }}
      </a>
    </footer>
  </div>

  <DebugFooter :show-ip="true" :show-time="true" />
</template>

<style scoped>
.screen-container {
  @apply grid min-h-screen place-items-center dark:bg-gray-800 dark:text-white;
}
</style>
